import React from 'react';
import { formatCurrencyES, formatPeriodES, TABLE_HEADERS_ES } from '../../../config/reportTranslations';
import './Cuadro04.css';

/**
 * Cuadro 04: Balance Evolution Table (USD millions)
 *
 * Displays historical balance sheet evolution:
 * - Period columns (9 months)
 * - Activo (Assets)
 * - Pasivo (Liabilities)
 * - Patrimonio (Equity)
 */

export interface Cuadro04Data {
  period: string;           // YYYY-MM format
  activo: number;          // Total assets
  pasivo: number;          // Total liabilities
  patrimonio: number;      // Total equity
}

export interface Cuadro04Props {
  data: Cuadro04Data[];
  title?: string;
}

export const Cuadro04: React.FC<Cuadro04Props> = ({
  data,
  title = 'Cuadro 04: Evolución Balance USD millones'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="cuadro-04-container">
        <h3 className="cuadro-04-title">{title}</h3>
        <p className="cuadro-04-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  // Sort data by period (newest first)
  const sortedData = [...data].sort((a, b) => b.period.localeCompare(a.period));

  // Convert to millions
  const toMillions = (value: number): number => value / 1_000_000;

  return (
    <div className="cuadro-04-container">
      <h3 className="cuadro-04-title">{title}</h3>

      <div className="cuadro-04-table-wrapper">
        <table className="cuadro-04-table">
          <thead>
            <tr>
              <th className="cuadro-04-header-period">{TABLE_HEADERS_ES.cuadro_04.periodo}</th>
              <th className="cuadro-04-header-numeric">{TABLE_HEADERS_ES.cuadro_04.activo}</th>
              <th className="cuadro-04-header-numeric">{TABLE_HEADERS_ES.cuadro_04.pasivos}</th>
              <th className="cuadro-04-header-numeric">{TABLE_HEADERS_ES.cuadro_04.patrimonio}</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, index) => (
              <tr key={row.period} className={index === 0 ? 'cuadro-04-row-current' : ''}>
                <td className="cuadro-04-cell-period">{formatPeriodES(row.period)}</td>
                <td className="cuadro-04-cell-numeric">{formatCurrencyES(toMillions(row.activo))}</td>
                <td className="cuadro-04-cell-numeric">{formatCurrencyES(toMillions(row.pasivo))}</td>
                <td className="cuadro-04-cell-numeric">{formatCurrencyES(toMillions(row.patrimonio))}</td>
              </tr>
            ))}
          </tbody>

          {/* Summary row - show totals verification (Activo = Pasivo + Patrimonio) */}
          <tfoot>
            <tr className="cuadro-04-footer">
              <td className="cuadro-04-cell-period" style={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                Nota: Activo = Pasivo + Patrimonio
              </td>
              <td colSpan={3}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Source note */}
      <p className="cuadro-04-source">
        Fuente: Estados financieros de la institución reportados a la Superintendencia
      </p>
    </div>
  );
};

export default Cuadro04;
